<script setup lang="ts">
import type { JSX } from '@ionic/core'
import { parseDate } from '~/utils/date'

const modelValue = defineModel<string>()
const props = withDefaults(defineProps<JSX.IonInput & {
	isRequired?: boolean
}>(),
{
	type: 'text',
	errorText: undefined,
})

// Ionic Input schreiben Leer Strings in das ModelValue, wenn man den Input leert.
// Für die Validierung und das Senden der Daten an das Backend ist es jedoch besser, wenn der Wert undefined ist.
const normalizedValue = computed({
	get: () => modelValue.value === '' ? undefined : modelValue.value,
	set: (v) => {
		modelValue.value = v === '' ? undefined : v
	},
})
const parsedDateValue = computed(() => normalizedValue.value === undefined ? undefined : parseDate(normalizedValue.value))
const resetValue = ref()
const openDate = ref(false)
const deactivateModal = ref(false)

const openModal = () => {
	if (props.readonly || props.disabled || deactivateModal.value) return
	resetValue.value = modelValue.value
	openDate.value = true
}
const closeModal = () => {
	deactivateModal.value = true
	openDate.value = false
	// Das zurücksetzen des Fokus auf den Input führt in iOS zu einem Klick-Event, welches das Modal direkt wieder öffnet. Durch die 300ms
	// wird dies verhindert, da das Modal in der Zeit nicht geöffnet werden kann.
	setTimeout(() => {
		deactivateModal.value = false
	}, 300)
}

const resetDate = () => {
	modelValue.value = resetValue.value
	closeModal()
}

const computedLabel = computed(() => {
	return props.label ? props.label + (props.isRequired ? '*' : '') : undefined
})

const invalid = ref(false)

watchDebounced(() => props.errorText, () => {
	invalid.value = !!props.errorText
}, { debounce: 400 })
</script>

<template>
	<ion-input
		v-bind.attr="$attrs"
		v-bind.prop="props"
		type="text"
		:data-cy="`date-input-${props.label}`"
		:model-value="parsedDateValue"
		:class="{ 'ion-invalid': !!invalid, 'ion-touched': !!invalid }"
		:error-text="errorText"
		:label="computedLabel"
		readonly
		aria-haspopup="dialog"
		@click="openModal"
	/>
	<ion-modal
		:is-open="openDate"
		css-class="date-modal"
		:data-cy="`date-modal-${props.label}`"
		@did-dismiss="closeModal"
	>
		<ion-content class="ion-padding">
			<ion-datetime
				v-model="normalizedValue"
				presentation="date"
			/>
			<div class="button-container">
				<ion-button
					expand="block"
					fill="clear"
					color="secondary"
					@click="resetDate"
				>
					Abbrechen
				</ion-button>
				<ion-button
					expand="block"
					fill="clear"
					color="secondary"
					@click="closeModal"
				>
					Fertig
				</ion-button>
			</div>
		</ion-content>
	</ion-modal>
</template>

<style lang="scss">
.date-modal {
	--width: 90%;
	--max-width: 320px;
	--max-height: 420px;
	--min-height: 380px;
	--border-radius: 14px;
	--box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;

	ion-content::part(scroll) {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding-bottom: 5px;
	}

	ion-datetime {
		--background: #ffffff;

		&::part(calendar-day) {
			font-size: 16px;
		}

		&::part(calendar-day active) {
			color: var(--ion-text-color);
			font-weight: 600;
		}
	}

	.button-container {
		display: flex;
		flex-direction: row;
		gap: 10px;
		justify-content: space-between;
	}
}
</style>
